import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AttendanceReport.css";

const API_URL = "https://aadyaacademy.org/api";

function AttendanceReport() {
  const today = new Date().toISOString().split("T")[0];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredAttendanceData, setFilteredAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState([]);
  const [popupTitle, setPopupTitle] = useState("");

  const formatDate = (inputDate) => {
    const date = new Date(inputDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const teacherPhone = localStorage.getItem("userPhone");
      if (!teacherPhone) {
        console.error("Teacher phone number not found in local storage.");
        setLoading(false);
        return;
      }

      const [classRes, sectionRes, attendanceRes] = await Promise.all([
        axios.get(`${API_URL}/classes`),
        axios.get(`${API_URL}/sections`),
        axios.get(`${API_URL}/teacher-attendance-reports`, {
          params: { phone: teacherPhone, date: selectedDate },
        }),
      ]);

      const classData = Array.isArray(classRes.data)
        ? classRes.data
        : classRes.data.classes || [];

      const sectionData = Array.isArray(sectionRes.data)
        ? sectionRes.data
        : sectionRes.data.sections || [];

      const attendanceFormatted = (attendanceRes.data.attendance_summary || []).map(
        (item, index) => ({
          id: index + 1,
          class: item.class,
          section: item.section,
          date: item.date,
          status: item.status,
          present: item.present || 0,
          absent: item.absent || 0,
        })
      );

      const sortedAttendance = attendanceFormatted.sort((a, b) =>
        parseInt(a.class) - parseInt(b.class)
      );

      setClasses(classData);
      setSections(sectionData);
      setAttendanceData(sortedAttendance);
      setFilteredAttendanceData(sortedAttendance);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowStudentList = async (item, type) => {
    try {
      const teacherPhone = localStorage.getItem("userPhone");
      if (!teacherPhone) {
        console.error("Teacher phone number not found in local storage.");
        return;
      }

      const res = await axios.get(`${API_URL}/attendance/students`, {
        params: {
          class: item.class,
          section: item.section,
          date: item.date,
          phone: teacherPhone,
        },
      });

      const filtered = (res.data || []).filter(
        (student) =>
          (type === "present" && student.status === "Present") ||
          (type === "absent" && student.status === "Absent")
      );

      setPopupTitle(
        `${type === "present" ? "Present" : "Absent"} Students - ${item.class} ${item.section} (${formatDate(item.date)})`
      );
      setPopupData(filtered);
      setShowPopup(true);
    } catch (error) {
      console.error("Error fetching student list:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  useEffect(() => {
    let filtered = [...attendanceData];

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.section.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedClass) {
      filtered = filtered.filter((item) => item.class === selectedClass);
    }

    if (selectedSection) {
      filtered = filtered.filter((item) => item.section === selectedSection);
    }

    if (selectedDate) {
      filtered = filtered.filter((item) => item.date === selectedDate);
    }

    filtered = filtered.sort((a, b) => parseInt(a.class) - parseInt(b.class));

    setFilteredAttendanceData(filtered);
  }, [searchTerm, selectedClass, selectedSection, selectedDate, attendanceData]);

  return (
    <div className="attendance-report">
      <div className="table-header">
        <h3>Attendance Report</h3>
        <div className="table-header-btm">
          <input
            type="date"
            className="date-picker"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <select
            className="class-filter"
            onChange={(e) => setSelectedClass(e.target.value)}
            value={selectedClass}
          >
            <option value="">All Classes</option>
            {classes
              .sort((a, b) => parseInt(a.name || a) - parseInt(b.name || b))
              .map((cls, index) => (
                <option key={index} value={cls.name || cls}>
                  {cls.name || cls}
                </option>
              ))}
          </select>
          <select
            className="section-filter"
            onChange={(e) => setSelectedSection(e.target.value)}
            value={selectedSection}
          >
            <option value="">All Sections</option>
            {sections.map((section, index) => (
              <option key={index} value={section.name || section}>
                {section.name || section}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="attendance-table-wrapper">
          <table>
            <thead>
              <tr>
                <th className="thead-content">Class</th>
                <th className="thead-content">Section</th>
                <th className="thead-content">Date</th>
                <th className="thead-content">Status</th>
                <th className="thead-content">Present</th>
                <th className="thead-content">Absent</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttendanceData.length > 0 ? (
                filteredAttendanceData.map((item) => (
                  <tr key={item.id}>
                    <td className="tbody-content">{item.class}</td>
                    <td className="tbody-content">{item.section}</td>
                    <td className="tbody-content">{formatDate(item.date)}</td>
                    <td className="tbody-content">
                      <span
                        className={`status-badge ${
                          item.status === "Updated" ? "updated" : "not-updated"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="tbody-content">
                      <span
                        style={{ color: "green", cursor: "pointer" }}
                        onClick={() => handleShowStudentList(item, "present")}
                      >
                        {item.present}
                      </span>
                    </td>
                    <td className="tbody-content">
                      <span
                        style={{ color: "red", cursor: "pointer" }}
                        onClick={() => handleShowStudentList(item, "absent")}
                      >
                        {item.absent}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="tbody-content">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <div className="popup-header">
              <h3>{popupTitle}</h3>
              <button onClick={() => setShowPopup(false)} className="close-btn">
                ✕
              </button>
            </div>
            {popupData.length > 0 ? (
              <ul className="popup-list">
                {popupData.map((student, index) => (
                  <li key={index}>{student.name}</li>
                ))}
              </ul>
            ) : (
              <p>No students found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendanceReport;
